/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import ProgressModal from '@/components/AZCustomComponent/ProgressModal';
import { useLabels } from '@/hooks/useLabels';
import { useFirstScrollDetected } from '@/hooks/useFirstScrollDetected';
import { useDeviceType } from '@/utils/useDeviceType';
import { Select, Text, View } from '@az/starc-ui';
import dynamic from 'next/dynamic';
import React, { useMemo } from 'react';
import { useReviewsAuthors } from '../../api/getReviewsAuthors';
import { type ActiveImage } from '../../hooks/useReviewImageModal';
import type { ProductReview, ReviewsSorting } from '../../interface';
import { CustomerReview } from '../CustomerReview/CustomerReview';
import { InlineFilters } from '../InlineFilters/InlineFilters';
import { CustomOption } from './components/CustomOption/CustomOption';
import styles from './styles.module.scss';

const LazyPagination = dynamic(() =>
  import('@az/starc-ui/dist/components/pagination').then((mod) => mod.Pagination)
);

export type ReviewsListProps = {
  reviews: ProductReview[] | undefined;
  pageInformation: string | null;
  totalPages: number | undefined;
  pageNumber: number;
  setPageNumber: React.Dispatch<React.SetStateAction<number>>;
  sortBy: ReviewsSorting | undefined;
  setSortBy: React.Dispatch<React.SetStateAction<ReviewsSorting | undefined>>;
  starFilter: number[];
  setStarFilter: React.Dispatch<React.SetStateAction<number[]>>;
  loading: boolean;
  onReviewImageClick: (image: ActiveImage) => void;
};

const labelMap = {
  sortBy: 'label_reviews_sortBy',
  sorting_mostRecent: 'label_reviews_sorting_mostRecent',
  sorting_mostHelpful: 'label_reviews_sorting_mostHelpful',
  sorting_mostRelevant: 'label_reviews_sorting_mostRelevant',
  sorting_descendingStar: 'label_reviews_sorting_descendingStar',
  sorting_ascendingStar: 'label_reviews_sorting_ascendingStar',
};

export function ReviewsList({
  reviews,
  pageInformation,
  totalPages,
  pageNumber,
  setPageNumber,
  sortBy,
  setSortBy,
  starFilter,
  setStarFilter,
  loading,
  onReviewImageClick,
}: ReviewsListProps) {
  const labels = useLabels(labelMap);
  const isBot = useDeviceType() === 'bot';
  const scrolled = useFirstScrollDetected(isBot);

  const { sortOptions, sortOptionsObject } = useMemo(() => {
    const optionsObject: Record<ReviewsSorting, { value: ReviewsSorting; label: string }> = {
      mostRecent: { value: 'mostRecent', label: labels.sorting_mostRecent },
      mostRelevant: { value: 'mostRelevant', label: labels.sorting_mostRelevant },
      descendingStar: { value: 'descendingStar', label: labels.sorting_descendingStar },
      ascendingStar: { value: 'ascendingStar', label: labels.sorting_ascendingStar },
    };

    return {
      sortOptions: Object.values(optionsObject),
      sortOptionsObject: optionsObject,
    };
  }, [
    labels.sorting_ascendingStar,
    labels.sorting_descendingStar,
    labels.sorting_mostRecent,
    labels.sorting_mostRelevant,
  ]);

  const profileIDs = useMemo(() => {
    return (
      (reviews ?? []).map((review) => review.profileId).filter((id) => id) as string[]
    ).sort();
  }, [reviews]);

  const {
    data: authorData,
    isSuccess,
    isFetching: reviewAuthorsFetching,
  } = useReviewsAuthors({
    profileIDs,
    stats: ['Reviews'],
  });

  const shouldShowPagination =
    totalPages !== undefined && totalPages > 0 && pageNumber !== 0 && scrolled;

  return (
    <View as="section" className={styles.reviewsListSection} attributes={{ id: 'reviewsList' }}>
      <View className={styles.pageInformationContainer}>
        <View className={styles.pageInformation}>
          <View className={styles.pageText}>
            <Text
              weight="bold"
              attributes={{
                'data-testid': 'pageInformation',
              }}
            >
              {pageInformation}
            </Text>
          </View>

          <View>
            <Select
              className={styles.reviewsSorting}
              label={labels.sortBy}
              name="sorting"
              options={sortOptions}
              value={sortBy ? sortOptionsObject[sortBy] : null}
              onValueChange={(newValue) => {
                setSortBy(newValue?.value as ReviewsSorting);
              }}
              disabled={loading}
              customOptions={CustomOption}
            />
          </View>
        </View>

        {starFilter.length > 0 && (
          <View>
            <InlineFilters starFilter={starFilter} setStarFilter={setStarFilter} />
          </View>
        )}
      </View>

      <View>
        {pageNumber > 1 && (loading || reviewAuthorsFetching) && <ProgressModal noScroll />}
        <View as="ul">
          {reviews?.map((review) => {
            const profile = review.profileId ? authorData?.Results[review.profileId] : undefined;
            return (
              <View
                as="li"
                key={review.id}
                className={styles.customerReviewRow}
                attributes={{ 'data-testid': 'review' }}
              >
                <CustomerReview
                  review={review}
                  onImageClick={onReviewImageClick}
                  authorReviewsCount={
                    profile?.ReviewStatistics
                      ? profile.ReviewStatistics.TotalReviewCount
                      : isSuccess
                      ? 0
                      : undefined
                  }
                  authorVotesCount={
                    profile?.ReviewStatistics
                      ? profile.ReviewStatistics.HelpfulVoteCount +
                        profile.ReviewStatistics.NotHelpfulVoteCount
                      : isSuccess
                      ? 0
                      : undefined
                  }
                />
              </View>
            );
          })}
        </View>
      </View>

      {shouldShowPagination && (
        <View align="center" className={styles.paginationWrapper}>
          <LazyPagination
            initialPage={pageNumber}
            totalPages={totalPages}
            onPageChange={(currentPage) => {
              setPageNumber(currentPage);

              requestAnimationFrame(() => {
                const bodyRect = document.body.getBoundingClientRect();
                const listRect = document.getElementById('reviewsList')?.getBoundingClientRect();
                const headerRect = document
                  .getElementById('fixedHeaderWrapper')
                  ?.getBoundingClientRect();

                window.scrollTo({
                  top: (listRect?.top ?? 0) - bodyRect.top - (headerRect?.height ?? 0) - 16,
                  behavior: 'smooth',
                });
              });
            }}
          />
        </View>
      )}
    </View>
  );
}
